import {
  Box,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {SearchIcon} from '@chakra-ui/icons';
import {Results} from '@orama/orama';
import {HitItemCatalogue} from 'api-types/ffconnect';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHalStates} from '@synako/haligator';
import {useDebouncedChange, useTypedFilters} from '@synako/typed-path';
import {differenceInDays} from 'date-fns/differenceInDays';
import {range} from 'lodash-es';
import {FixedSizeGrid} from 'react-window';
import {
  coerceFiltresItemCatalogue as coerce,
  useLivraisonsPossiblesContext,
  usePanier,
  useSearchClient,
} from 'ff-common';
import {FiltresItemCatalogue} from 'ff-common/src/hooks/search/use-search';
import {FilterBar} from '@/components/FilterBar/FilterBar';
import {useScrollbarWidth} from '@/chakra/useScrollbarWidth';
import {BreakPointContext} from '@/chakra/BreakpointProvider';
import {useBlockScroll} from '@/backoffice/hooks/useBlockScroll';
import {
  RemplacementItem,
  RemplacementItemSkeleton,
} from '@/domains/panier/components/ModaleRemplacementCatalogue';
import {useAjouterAuPanierDepuisModale} from '@/hooks/panier/useAjouterAuPanierDepuisModale';
import {useHauteurVignetteRemplacement} from '@/hooks/panier/useVignetteRemplacement';

// Plafond de résultats pour la recherche dans l'UB : la grille est virtualisée
// sans pagination, on récupère donc tous les produits de l'UB qui matchent.
const LIMITE_PRODUITS_UB = 1000;

interface UbSidePanelPayload {
  nom: string;
  /** Matching par nomenclature (BCT / BVP / RC / RS). */
  categories?: string[];
  /** Matching par mots directeurs (CREMIERS). */
  mots_directeurs?: string[];
}

/**
 * Side panel "Voir tous les produits de l'UB" des UB Essentielles : liste tous
 * les produits du catalogue FF rattachés à l'UB (catégories std ou mots
 * directeurs), au-delà de l'historique d'achat, et permet de les ajouter au
 * panier sans fermer le panneau.
 */
const SidePanelProduitsUBContent: React.FC<{
  ub: UbSidePanelPayload;
}> = ({ub}) => {
  const modal = useModal();
  const {t} = useTranslation();
  const search = useSearchClient();
  const ajouterAuPanier = useAjouterAuPanierDepuisModale();

  const filtersHook = useTypedFilters<FiltresItemCatalogue>({q: ''}, coerce);
  const debouncedChange = useDebouncedChange(filtersHook, 'q', 300);
  const [resultats, setResultats] = useState<
    Results<HitItemCatalogue> | undefined
  >(undefined);

  // CREMIERS : mots_directeurs ; autres profils : catégories.
  const modeMotsDirecteurs = (ub.mots_directeurs?.length ?? 0) > 0;

  // Afficher la FilterBar dès qu'un filtre hors-texte est actif, pas seulement quand du texte est saisi — sinon le filtre
  // devient invisible et impossible à retirer.
  const p = filtersHook.params;
  const hasFiltresHorsTexte = !!(
    p.nature_du_prix ??
    p.labels?.length ??
    p.temperature_livraison ??
    p.famille_fromage?.length ??
    p.type_de_lait?.length ??
    p.traitement_thermique_du_lait?.length
  );

  // Date de livraison pour exclure les précommandes en A pour B (livraison ≤ J+1).
  const {date_de_livraison: dateContext} = useLivraisonsPossiblesContext();
  const date_de_livraison =
    dateContext || new Date().toISOString().split('T')[0];
  const ApourB = differenceInDays(date_de_livraison, new Date()) <= 1;

  // Lignes du panier pour désactiver le bouton des articles déjà ajoutés
  // (le panel reste ouvert : on enchaîne les ajouts).
  const lignesDePanier =
    useHalStates(usePanier().followAll('lignes_de_panier')).states ?? [];
  const isDisabled = (code_article: string) =>
    lignesDePanier.some((l) => l.data.code_article === code_article);

  // Re-render différé après l'ouverture du drawer (transition + montage du conteneur) :
  // permet de mesurer sa largeur (`usableWidth`, lu pendant le render) — sinon la grille
  // reste bloquée sur les skeletons.
  const [goRender, setGoRender] = useState(false);
  useEffect(() => {
    if (modal.visible) {
      setTimeout(() => {
        setGoRender(true);
      }, 600);
    }
  }, [modal.visible]);

  useEffect(() => {
    if (!goRender || !search.indexIsBuilt) return;
    const hasFilters = hasFiltresHorsTexte || !!filtersHook.params.q;
    if (hasFilters) {
      // Recherche / filtre actif : `cat_std_any` ou `mot_directeur_any` borne
      // nativement la recherche au périmètre de l'UB (sémantique ANY). Les hits,
      // le `count` et les `facets` (FilterBar) sont déjà restreints — pas de
      // post-filtre.
      search
        .searchItems(
          {
            nature_du_prix: filtersHook.params.nature_du_prix,
            labels: filtersHook.params.labels,
            temperature_livraison: filtersHook.params.temperature_livraison,
            famille_fromage: filtersHook.params.famille_fromage,
            type_de_lait: filtersHook.params.type_de_lait,
            traitement_thermique_du_lait:
              filtersHook.params.traitement_thermique_du_lait,
            q: filtersHook.params.q,
            ...(modeMotsDirecteurs
              ? {mot_directeur_any: ub.mots_directeurs}
              : {cat_std_any: ub.categories}),
            precommande: ApourB ? false : undefined,
            afficher_marketplace: false,
          },
          LIMITE_PRODUITS_UB,
        )
        .then(setResultats);
    } else if (modeMotsDirecteurs) {
      search
        .articlesPourMotsDirecteursUB(ub.mots_directeurs!, {
          enleverPrecommande: ApourB,
        })
        .then(setResultats);
    } else {
      search
        .articlesPourCategoriesUB(ub.categories ?? [], {
          enleverPrecommande: ApourB,
        })
        .then(setResultats);
    }
    // Deps volontairement réduites : recalcul sur changement de filtres
    // (`filtersHook.params`), ouverture du drawer (`goRender`) ou index Orama prêt
    // (`search.indexIsBuilt`). Props UB et `ApourB` figés tant que le drawer est
    // ouvert → inutiles ici ; les ajouter forcerait à lister `search.*` → boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersHook.params, goRender, search.indexIsBuilt]);

  useBlockScroll(modal.visible);

  const hitsToRender = resultats?.hits ?? [];

  const {isMobile} = useContext(BreakPointContext)!;
  // Même vignette que la modale de remplacement, donc même hauteur de case.
  const hauteurVignette = useHauteurVignetteRemplacement(isMobile);
  const scollW = useScrollbarWidth();
  const boxRef = useRef<HTMLDivElement | null>(null);
  // Largeur du conteneur, lue pendant le render. Le re-render provoqué par `goRender`
  // (après l'ouverture du drawer) garantit que `boxRef` est monté à ce moment, donc
  // que `clientWidth` est mesurable — sinon la grille reste sur les skeletons.
  const usableWidth = boxRef.current?.clientWidth;
  const columnCount = isMobile ? 1 : 3;
  const columnWidth = isMobile
    ? usableWidth
    : Math.floor((usableWidth ?? 100 - scollW) / 3);

  return (
    <Drawer
      placement='right'
      isOpen={modal.visible}
      onClose={() => {
        modal.remove();
      }}
      size='xl'
    >
      <DrawerOverlay />
      <DrawerContent
        position='relative'
        bg='white'
        maxW='740px'
        transition='all 0.2s ease-out'
      >
        <VStack
          height='100%'
          spacing={0}
        >
          <HStack
            p='4'
            bg='blue.main'
            color='white'
            position='sticky'
            top='0'
            zIndex='sticky'
            w='100%'
            spacing={4}
          >
            <VStack
              flex={1}
              alignItems='flex-start'
            >
              <Text>{t('commande.voir_produits_ub', {nom: ub.nom})}</Text>
            </VStack>
            <Box>
              <DrawerCloseButton position='static' />
            </Box>
          </HStack>
          <Box
            w='100%'
            mx='auto'
          >
            <VStack
              spacing={0}
              align='stretch'
              w='100%'
            >
              <Box
                bg='gray.light'
                p='5'
              >
                <InputGroup
                  zIndex={1003}
                  bg='white'
                  borderRadius='8px'
                >
                  <InputLeftElement
                    pointerEvents='auto'
                    cursor='pointer'
                  >
                    <SearchIcon />
                  </InputLeftElement>
                  <Input
                    placeholder={t('commande.chercher_dans_ub')}
                    onChange={(e) => {
                      debouncedChange.change(e.target.value);
                    }}
                    value={debouncedChange.value}
                  />
                </InputGroup>
              </Box>
              {(!!debouncedChange.value || hasFiltresHorsTexte) && (
                <Box
                  pt='3'
                  pb='3'
                  pr={{base: '5', md: '2'}}
                  pl={{base: '5', md: '2'}}
                  w='100%'
                >
                  <FilterBar
                    itemResults={resultats}
                    filters={filtersHook.params}
                    onFilterChange={(key, value) => {
                      filtersHook.change(key, value as never);
                    }}
                    onRemoveAllFilters={(preservePromo) => {
                      filtersHook.changeMultiple({
                        nature_du_prix: preservePromo ? 'promo' : undefined,
                        labels: undefined,
                        temperature_livraison: undefined,
                        famille_fromage: undefined,
                        type_de_lait: undefined,
                        traitement_thermique_du_lait: undefined,
                      });
                    }}
                    direction='column-reverse'
                    containerProps={{bg: 'transparent', p: '0!important'}}
                  />
                </Box>
              )}
            </VStack>
          </Box>
          <Box
            flex={1}
            w='100%'
            mb='0'
            ref={boxRef}
            overflow='auto'
          >
            {resultats?.hits.length === 0 ? (
              <Text
                textAlign='center'
                my='10'
                fontSize='xl'
                color='gray.dark'
              >
                {t('commande.aucun_produit_ub')}
              </Text>
            ) : !usableWidth ? (
              <SimpleGrid
                columns={{base: 1, md: 3}}
                spacing='0'
                p='20px'
                gap='0'
              >
                {range(0, 6).map((_, i) => (
                  <RemplacementItemSkeleton key={i} />
                ))}
              </SimpleGrid>
            ) : (
              <FixedSizeGrid
                rowHeight={hauteurVignette}
                columnWidth={columnWidth!}
                columnCount={columnCount}
                height={boxRef.current?.clientHeight ?? 400}
                width={usableWidth}
                rowCount={Math.max(
                  1,
                  Math.ceil(hitsToRender.length / columnCount),
                )}
              >
                {({rowIndex, columnIndex, style}) => {
                  const index = rowIndex * columnCount + columnIndex;
                  const hit = hitsToRender[index];
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  if (!hit) {
                    return null;
                  }
                  return (
                    <RemplacementItem
                      key={hit.id}
                      hit={hit.document}
                      // Ajout au panier sans fermer le panel : on enchaîne les ajouts,
                      // le bouton de l'article ajouté passe en "déjà au panier".
                      remplacerClick={(itemState) => {
                        void ajouterAuPanier(itemState.data.code);
                      }}
                      style={style}
                      isMobile={isMobile}
                      mode='add-to-panier'
                      isDisabled={isDisabled}
                    />
                  );
                }}
              </FixedSizeGrid>
            )}
          </Box>
        </VStack>
      </DrawerContent>
    </Drawer>
  );
};

export const SidePanelProduitsUB = NiceModal.create<{
  ub: UbSidePanelPayload;
}>((props) => <SidePanelProduitsUBContent {...props} />);
